import {FC, ReactNode} from 'react'
import {useQuery} from 'react-query'
import {onTSP} from '../../../../../../_metronic/helpers'
import {useAuth} from '../../../../auth'
import {useIsOnPremises} from '../../../../auth/core/useOnPremises'
import {
  findFeature,
  getServiceCatalog,
  serviceCatalogKey,
  ServiceFeatureKey,
} from '../../../../business-services/core/catalogApi'
import {needsContact} from '../../../../business-services/core/serviceApi'
import {ServiceActivationCard} from '../../../../business-services/components/ServiceActivationCard'
import {ServiceFeeLine} from '../../../../business-services/components/ServiceFeeLine'
import './CostFormServiceGate.css'

// «فرم قیمت تمام‌شده» and «… پیشرفته» are value-added services (user request 2026-10-18), in «تماس بگیرید» until an
// administrator prices them. The form shows why it is closed, and how to open it, before anything is typed into it;
// the server refuses a save from a closed form all the same.

const COST_FORM_FEATURES: Readonly<Record<number, ServiceFeatureKey>> = {
  2: 'costPriceForm',
  3: 'advancedCostPriceForm',
}

/** The service of the invoice form (2 «قیمت تمام‌شده», 3 «… پیشرفته»); undefined for the free forms. */
export const costFormFeature = (formType: number): ServiceFeatureKey | undefined => COST_FORM_FEATURES[formType]

/**
 * Whether the business may use each cost-price form now, for the form picker's words: undefined while unknown (an
 * older backend, an on-premises install or a read that failed), when the server's own refusal stays the answer.
 */
export const useCostFormAccess = (): Record<number, boolean> | undefined => {
  const {currentUser} = useAuth()
  const onPremises = useIsOnPremises()
  const companyId = Number(currentUser?.cpi || 0)
  const catalog = useQuery(serviceCatalogKey(companyId), () => getServiceCatalog(companyId), {
    enabled: companyId > 0 && !onPremises && !onTSP,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  })
  if (!catalog.data) return undefined
  const access: Record<number, boolean> = {}
  Object.entries(COST_FORM_FEATURES).forEach(([formType, feature]) => {
    const item = findFeature(catalog.data, feature)
    if (item) access[Number(formType)] = item.active
  })
  return access
}

type Props = {
  formType: number
  /** «ادامه با فرم کامل»: the full form is free for everyone. */
  onUseFullForm: () => void
  children: ReactNode
}

export const CostFormServiceGate: FC<Props> = ({formType, onUseFullForm, children}) => {
  const {currentUser} = useAuth()
  const onPremises = useIsOnPremises()
  const companyId = Number(currentUser?.cpi || 0)
  const feature = costFormFeature(formType)
  const enabled = Boolean(feature) && companyId > 0 && !onPremises && !onTSP
  const catalog = useQuery(serviceCatalogKey(companyId), () => getServiceCatalog(companyId), {
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  })

  if (!enabled || !feature) return <>{children}</>
  if (catalog.isLoading) {
    return (
      <div className='mp-cost-form-gate__loading' role='status'>
        <span className='spinner-border spinner-border-sm' aria-hidden='true' />
        در حال بررسی دسترسی به این فرم…
      </div>
    )
  }

  const item = findFeature(catalog.data, feature)
  // An older backend without the service, or a catalog that could not be read: the form opens and the server decides.
  if (!item) return <>{children}</>

  if (item.active) {
    return (
      <>
        <ServiceFeeLine companyId={companyId} feature={feature} each='هر صورتحساب تازه با این فرم' className='mb-4' />
        {children}
      </>
    )
  }

  const contact = needsContact(item.tariff)
  return (
    <section className='mp-cost-form-gate' aria-labelledby='mp-cost-form-gate-title'>
      <div className='mp-cost-form-gate__head'>
        <i className='las la-lock' aria-hidden='true' />
        <div>
          <h3 id='mp-cost-form-gate-title'>«{item.tariff.label}» برای این کسب‌وکار فعال نیست</h3>
          <p>
            {contact
              ? 'این فرم سرویس ویژه است و با درخواست شما از سوی پشتیبانی فعال می‌شود. تا آن زمان صورتحساب را با فرم کامل صادر کنید؛ فرم کامل برای همه رایگان است.'
              : 'این فرم سرویس ویژه است؛ پس از فعال‌سازی، هزینهٔ آن طبق تعرفه از کیف پول کسب‌وکار کم می‌شود. تا آن زمان صورتحساب را با فرم کامل صادر کنید.'}
          </p>
        </div>
      </div>
      <ServiceActivationCard companyId={companyId} feature={feature} activation={!contact} className='mb-4' />
      <button type='button' className='btn btn-primary' onClick={onUseFullForm}>
        <i className='las la-file-invoice fs-3' aria-hidden='true' />
        ادامه با فرم کامل
      </button>
    </section>
  )
}
